import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAuth, api } from '../context/AuthContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

const Input = styled('input')({
  display: 'none',
});

const CreateMemorial = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    dateOfDeath: '',
    biography: '',
  });
  const [qrCode, setQrCode] = useState('');
  const [qrCodeValid, setQrCodeValid] = useState(true);
  const [qrCodeMessage, setQrCodeMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewUrls, setPreviewUrls] = useState([]);
  
  const goBack = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateQrCode = async (code) => {
    if (!code) {
      setQrCodeValid(false);
      setQrCodeMessage('QR code is required');
      return false;
    }
    
    try {
      const response = await api.get(`/api/qrcodes/validate/${code}`);
      if (response.data.valid) {
        if (response.data.isAssigned) {
          setQrCodeValid(false);
          setQrCodeMessage('This QR code has already been assigned to a memorial');
          return false;
        } else {
          setQrCodeValid(true);
          setQrCodeMessage('QR code is valid');
          return true;
        }
      } else {
        setQrCodeValid(false);
        setQrCodeMessage('Invalid QR code');
        return false;
      }
    } catch (err) {
      setQrCodeValid(false);
      setQrCodeMessage('Error validating QR code');
      return false;
    }
  };
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Cleanup old preview URLs
    previewUrls.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    const newUrls = files.map(file => URL.createObjectURL(file));
    setSelectedFiles(files);
    setPreviewUrls(newUrls);
  };

  const uploadFiles = async (memorialId) => {
    if (selectedFiles.length === 0) return;
    
    const formData = new FormData();
    formData.append('memorialId', memorialId);
    
    // Append all files with the key 'files'
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
  
    return api.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${auth.token}`
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.name || !formData.dateOfBirth || !formData.dateOfDeath || !formData.biography || !qrCode) {
      setError('Please fill in all required fields including QR code');
      setLoading(false);
      return;
    }

    // Validate QR code
    const isQrValid = await validateQrCode(qrCode);
    if (!isQrValid) {
      setLoading(false);
      return;
    }

    try {
      console.log('Sending memorial data:', { ...formData, qrCode }); // Debug log

      const memorialResponse = await api.post('/api/memorials', {
        name: formData.name,
        dateOfBirth: formData.dateOfBirth,
        dateOfDeath: formData.dateOfDeath,
        biography: formData.biography,
        qrCode: qrCode
      });
      console.log('Memorial creation response:', memorialResponse.data);

      if (selectedFiles && selectedFiles.length > 0) {
        try {
          await uploadFiles(memorialResponse.data.memorial._id);
        } catch (uploadError) {
          console.error('File upload error:', uploadError);
          // Continue even if file upload fails
        }
      }
      setSuccess('Memorial created successfully!');

      // Clean up URLs and reset form
      previewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      setSelectedFiles([]);
      setPreviewUrls([]);
      setFormData({
        name: '',
        dateOfBirth: '',
        dateOfDeath: '',
        biography: '',
      });
      setQrCode('');

      // Navigate after successful creation
      setTimeout(() => {
        navigate(`/memorial/${memorialResponse.data.memorial._id}`);
      }, 1500);
    } catch (err) {
      console.error('Error creating memorial:', err.response || err); // Debug log
      setError(
        err.response?.data?.message || 
        'Failed to create memorial. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [previewUrls]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <IconButton onClick={goBack} sx={{ mb: 2 }}>
        <ArrowBackIcon />
      </IconButton>

      <StyledPaper elevation={3}>
        <Typography variant="h4" align="center" gutterBottom>
          Create a Memorial
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name of Deceased"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Birth Date"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Death Date"
                name="dateOfDeath"
                type="date"
                value={formData.dateOfDeath}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Biography"
                name="biography"
                value={formData.biography}
                onChange={handleChange}
                multiline
                rows={4}
                required
              />
            </Grid>
            
            {/* QR Code field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="QR Code"
                value={qrCode}
                onChange={(e) => {
                  setQrCode(e.target.value);
                  if (e.target.value) {
                    validateQrCode(e.target.value);
                  } else {
                    setQrCodeValid(true);
                    setQrCodeMessage('');
                  }
                }}
                error={!qrCodeValid}
                helperText={qrCodeMessage || "Enter the QR code provided with your purchase"}
                required
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Upload Photos
                  </Typography>
                  <Box sx={{ textAlign: 'center' }}>
                    <label htmlFor="contained-button-file">
                      <Input
                        accept="image/*"
                        id="contained-button-file"
                        multiple
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        sx={{ mt: 2 }}
                      >
                        Select Photos
                      </Button>
                    </label>
                    {selectedFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography>
                          {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 2 }}>
                          {previewUrls.map((url, index) => (
                            <Grid item xs={4} key={index}>
                              <img
  src={url}
  alt={`Preview ${index + 1}`}
  loading="lazy"
  style={{
    width: '100%',
    height: 150,
    objectFit: 'cover',
    borderRadius: 4,
  }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              sx={{ minWidth: 200 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create Memorial'
              )}
            </Button>
          </Box>
        </form>
      </StyledPaper>
    </Container>
  );
};

export default CreateMemorial;